import type { CarePlanId } from "./care-plan.model";
import { ChangeLogAccumulator } from "./change-log.model";
import { ENVELOPE_DURATION_CAP_MINUTES, ENVELOPE_DURATION_INCREMENT_MINUTES } from "./envelope-duration";
import { PreconditionFailedError } from "./errors";
import { InvalidOrderSetError } from "./errors.model";
import type { Brand } from "./lib/brand";
import type { VersionedCanonical } from "./lib/canonical";
import type { DomainId } from "./lib/domain-id";
import { type ObservationLevel, observationLevelsProblem } from "./lib/observation-level";
import { draftBaselineOf, logsWhileDrafting, restoreDraftBaseline } from "./lib/order-set-draft";
import { type PlannedTrial, plannedTrialsEqual, plannedTrialsProblem } from "./lib/planned-trial";
import { type MemberId, memberIdSequence } from "./member-identity";
import type { PatientId } from "./patient.model";
import { type MaterializableDefinition, materializeDefinition } from "./spine.model";
import {
  releaseStepTime,
  reorderStep as reorderPlacedStep,
  resolvePlacement,
  type StepPlacement,
  type StepPlacementProps,
  settle,
} from "./step-placement";
import {
  type AnsweredValue,
  answeredValueProblem,
  answersEqual,
  type ParameterAnswer,
  type ParameterSource,
  type ParameterValue,
} from "./value.model";
import type { Visit, VisitId } from "./visit.model";

/** Stable OrderSet domain identity. Deliberately unrelated to RequestGroup.id. */
export type OrderSetId = DomainId<"OrderSetId">;

/** Boundary for a persisted stable OrderSet identity. */
export function asOrderSetId(value: string): OrderSetId {
  if (!value) throw new PreconditionFailedError("An OrderSet identity cannot be empty");
  return value as OrderSetId;
}

// ============================================================
// OrderSet — the body of clinical work for one visit
//
// NOT A CATALOG MODEL. It is the OUTPUT side, where the pin lives. A set
// ORIGINATED FROM a protocol rather than being an instance of one: curation
// adds, revokes and re-sequences, so a set can end up matching no protocol
// that exists. It is `protocol + delta`, and adding a fourth procedure to a
// three-procedure set does not negate the protocol — the set is still that
// protocol, plus this.
//
// PINS, NEVER SNAPSHOTS. `frozenDefinition` is deleted. An order pins
// `url|version` and nothing more: a revision is immutable, so a snapshot
// defended against a mutation the catalog model has made impossible. What
// stays on the order is `parameterValues[]` and the pin — the
// choices made AT materialization, which are genuinely the order's own.
//
// The set does not copy declarations, but it still owns the invariant. A
// command that changes an answer receives the pinned definition's effective
// declarations whenever an answer is added or changed. This keeps stored
// orders lean without making definition checks optional.
//
// IT REFERENCES WORK AND OWNS NO RESULTS. Findings live on the Exam,
// measurements on Observations, execution on Procedure — each pointing back at
// the order it fulfils. Recording a finding and re-sequencing a visit are
// different acts, at different times, by different people; one object means
// writing a finding contends with the whole set, and the set grows all day.
//
// THE SET AND THE VISIT HAVE INDEPENDENT LIFECYCLES. Check-in stamps the
// execution onto the orders so charting can find them, and does nothing else —
// a set can be complete while the visit is not.
// ============================================================

/** Loaded booking facts, never persisted as aggregate workflow state. */
export interface OrderSetSchedulingFacts {
  booked: boolean;
}

/**
 * OrderSets start active. `draft` is an unbooked set mid-curation: its edits are
 * saved as they are made, but it is out of booking until Save returns it to
 * `active`, and Discard can still restore what it held before.
 */
export const ORDER_SET_STATUSES = ["draft", "active", "completed", "revoked"] as const;
const EMPTY_ORDER_SET_ENVELOPE_MINUTES = 5;
export type OrderSetStatus = (typeof ORDER_SET_STATUSES)[number];
/** Statuses of a set still on the plan: being curated, or ready to book. */
export const CURRENT_ORDER_SET_STATUSES = ["draft", "active"] as const satisfies readonly OrderSetStatus[];
export type CurrentOrderSetStatus = (typeof CURRENT_ORDER_SET_STATUSES)[number];
// The same values as a set's by coincidence, not derivation: an order's `draft` is
// FHIR's planned-not-done, a set's is unsaved curation. Do not alias the two.
export const ORDER_STATUSES = ["draft", "active", "completed", "revoked"] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

// PARAMETER ANSWERS ARE `ParameterAnswer`, SHARED WITH THE PROTOCOL OCCURRENCE
// deliberately: materialization copies an answer across without translating
// it, and one shape is what makes that a copy rather than a conversion that can
// lose something. Identity not name, typed not `{ code, display }`, no label
// stored, and absent meaning unanswered — all of it defined once, in
// `value.model.ts`, beside the declarations those answers answer.
//
// `source` is what lets a procedure's own parameters and its required device's
// inherited ones coexist: the two sets stay in SEPARATE NAMESPACES, so a
// treatment's `p-1` and its laser's `p-1` cannot collide, and there is nothing
// to check because nothing can collide.

/**
 * One requested procedure. A member, not an aggregate: an order has no life
 * apart from the set that requested it.
 *
 * CODELESS. An order carries no clinical code. What was requested is the
 * pinned canonical, which is more precise than a code — a code says "a retinal
 * scan", the pin says "revision 4 of this retinal scan, with these parameters".
 * `ServiceRequest.code` is 0..1 and `instantiates-canonical` is a search
 * parameter, so dropping the code costs no queryability, which was the only
 * real argument for keeping it. When a billing or outbound interface arrives
 * the code goes on the DEFINITION and flows to the order, landing in both
 * places from one revision.
 */
export interface Order {
  /** Stable member identity used by commands and change-log entries. */
  orderId: OrderId;
  /** The catalog procedure this realizes, pinned to an exact revision. */
  definition: VersionedCanonical;
  /**
   * The protocol occurrence that produced this order. PRESENT IFF PROTOCOL-
   * DERIVED, and its absence is the only signal that an order was
   * curation-added.
   *
   * Matching by definition url instead is ambiguous exactly when it matters:
   * two occurrences of "Treatment A" at the same revision are byte-identical,
   * so after a swap nothing says which moved. The qualifier does not solve it
   * either — it is editable display text, not identity. Repeats are deliberate
   * and auto-append actively creates them, so this is not a rare case.
   */
  sourceOccurrenceId?: MemberId;
  /**
   * Display suffix that tells repeated orders of ONE definition apart, copied
   * from the protocol occurrence. Absent when nothing needs distinguishing.
   *
   * DISPLAY TEXT, NEVER IDENTITY — `sourceOccurrenceId` above is identity, and
   * the two are not alternatives. Without this a visit with four NINM
   * placements shows four rows reading "NINM", which is the exact confusion the
   * protocol's own uniqueness rule exists to prevent; carrying it as identity
   * instead would break the moment a clinician renamed it.
   */
  qualifier?: string;

  parameterValues: ParameterAnswer[];
  observationLevels?: readonly ObservationLevel[];
  /**
   * Copied from the protocol occurrence at materialization, ids unchanged, then
   * curated per patient. Absent when none. A plan, never a completion quota;
   * an order with planned trials answers nothing itself (Rule 7).
   */
  plannedTrials?: PlannedTrial[];
  status: OrderStatus;
  /**
   * When the order was withdrawn, retained as clinical history.
   */
  revokedAt?: string;
  /** The visit, stamped at check-in so charting can find the order. */
  visitId?: VisitId;
}

/** A group of orders that run concurrently. A one-order step is serial. */
interface OrderStep {
  id: MemberId;
  placement: StepPlacementProps;
  orders: Order[];
}

// ── the delta log ────────────────────────────────────────────────────────────

/**
 * Recorded changes to the requested work.
 *
 * ENTRIES ARE SELF-SUFFICIENT: each carries its own `from` and `to` VALUES,
 * never references, so a change still reads correctly after a parameter is
 * renamed or a definition revised. `OrderAdded` duplicates what the order
 * itself holds, and it has to — the log must still read correctly after that
 * order is revoked.
 *
 * A COMPUTED DIFF IS NOT A SUBSTITUTE. A diff answers "how does this differ
 * from the protocol now"; the log answers "what happened, in what order", and
 * added-then-removed is invisible to the first and visible to the second.
 *
 * The timed-work log distinguishes curation from charting an unfulfilled request.
 */
export interface OrderAdded {
  kind: "OrderAdded";
  envelopeGrewBy?: number;
  orderId: OrderId;
  definition: VersionedCanonical;
  step: number;
  parameterValues: readonly ParameterAnswer[];
}

interface OrderNotPerformed {
  kind: "OrderNotPerformed";
  orderId: OrderId;
}

interface OrderRevoked {
  kind: "OrderRevoked";
  orderId: OrderId;
}

export interface OrderMoved {
  kind: "OrderMoved";
  orderId: OrderId;
  fromStep: number;
  toStep: number;
  envelopeGrewBy?: number;
}

export interface ParameterChanged {
  kind: "ParameterChanged";
  orderId: OrderId;
  source: ParameterSource;
  parameterId: MemberId;
  from?: AnsweredValue;
  to: AnsweredValue | null;
}

export interface PlannedTrialsReplaced {
  kind: "PlannedTrialsReplaced";
  orderId: OrderId;
  from: readonly PlannedTrial[];
  to: readonly PlannedTrial[];
}

export interface StepMoved {
  kind: "StepMoved";
  step: number;
  from: number;
  to: number;
  envelopeGrewBy?: number;
}

export interface StepReordered {
  kind: "StepReordered";
  stepId: MemberId;
  from: number;
  to: number;
}

export interface StepResized {
  kind: "StepResized";
  step: number;
  from: number;
  to: number;
  envelopeGrewBy?: number;
}

/** Resolved from the pinned protocol, transient command facts only. */
export interface ResetTimingFacts {
  envelopeMinutes: number;
  placements: readonly { orderId: OrderId; placement: StepPlacementProps }[];
}

interface StepTiming {
  placement: StepPlacementProps;
  orders: { orderId: OrderId }[];
}

export interface TimingReset {
  kind: "TimingReset";
  from: StepTiming[];
  to: StepTiming[];
  envelopeGrewBy?: number;
}

export interface EnvelopeSet {
  kind: "EnvelopeSet";
  from: number;
  to: number;
}

export type OrderSetChange =
  | BookingShapeApplied
  | DraftSaved
  | EnvelopeSet
  | OrderAdded
  | OrderRevoked
  | OrderNotPerformed
  | OrderMoved
  | ParameterChanged
  | PlannedTrialsReplaced
  | StepMoved
  | StepReordered
  | StepResized
  | TimingReset;

/** The checked proposal is one curation act, including additions and removals. */
export interface BookingShapeApplied {
  kind: "BookingShapeApplied";
  from: Pick<OrderSetSnapshot, "envelopeMinutes" | "steps">;
  to: Pick<OrderSetSnapshot, "envelopeMinutes" | "steps">;
}

/** A saved draft is one curation act: edits made while drafting are logged here, not one by one. */
export interface DraftSaved {
  kind: "DraftSaved";
  /** Absent for a set saved for the first time. */
  from?: OrderSetDraftBaseline;
  to: OrderSetDraftBaseline;
}

/** What a draft replaced, so Discard can put it back. */
export type OrderSetDraftBaseline = Pick<OrderSetSnapshot, "envelopeMinutes" | "steps">;

// ── facts the set cannot reach ───────────────────────────────────────────────

/** The effective declarations resolved from an order's pinned procedure and device definitions. */
export interface OrderParameterDeclaration {
  source: ParameterSource;
  parameterId: MemberId;
  /** Carried for messages about this parameter; matching is always by id. */
  label: string;
  value: ParameterValue;
}

export interface OrderSetProps {
  /** Stable domain identity. Deliberately unrelated to the FHIR RequestGroup id. */
  id: OrderSetId;
  patientId: PatientId;
  /**
   * The protocol revision this set was realized from, when it was. Absent for
   * a set built procedure by procedure. Present, it pins a revision, so the set
   * keeps its provenance when the catalog moves.
   */
  protocol?: VersionedCanonical;
  /**
   * When the protocol was MATERIALIZED onto this set — the instant the pin was
   * taken, not when the set was last touched.
   *
   * Required for the same reason `protocol` is. The pin names a revision, but a
   * protocol's children are unpinned edges that move underneath it, so a
   * revision alone does not name a state of the graph. This instant does, and it
   * is the moment the set stopped following the catalog and started owning what
   * it had. Reading the set's protocol as of any other instant reads a protocol
   * that was never ordered.
   */
  envelopeMinutes: number;
  orderedAt: string;
  /** Care plan whose treatment-plan curation produced this set. Absent for standalone order sets. */
  carePlanId?: CarePlanId;
  /** Zero-based position among the care plan's curated sets. Present iff `carePlanId` is present. */
  curationRank?: number;
  status: OrderSetStatus;
  /** Present exactly while `status` is `draft`. */
  draftBaseline?: OrderSetDraftBaseline;
  /** Highest step identity issued, including steps removed by a timing reset. */
  stepIdHigh: number;
  steps: OrderStep[];
}

/** Scheduling-only proposal. Existing clinical facts are always read from the booked set. */
export interface OrderSetBookingShape {
  envelopeMinutes: number;
  steps: Array<{
    id: MemberId;
    placement: StepPlacementProps;
    orders: Array<{
      orderId: OrderId;
      definition?: VersionedCanonical;
      parameterValues?: ParameterAnswer[];
    }>;
  }>;
}

export type OrderSetSnapshot = OrderSetProps;

type PlacedOrderStep = Omit<OrderStep, "placement"> & { placement: StepPlacement };
type OrderSetState = Omit<OrderSetSnapshot, "steps"> & { steps: PlacedOrderStep[] };

/**
 * The aggregate. Extends the accumulator rather than owning a log field: every
 * curation command appends what it actually did, and the SESSION drains it at
 * commit and writes it in the same transaction bundle. The buffer is private
 * even from this class, which is how "the log is provenance, never state" is a
 * structural fact rather than a convention nobody can enforce.
 */
export class OrderSet extends ChangeLogAccumulator<OrderSetChange> {
  private state: OrderSetState;

  private constructor(props: OrderSetSnapshot) {
    super();
    if (!(ORDER_SET_STATUSES as readonly string[]).includes(props.status)) {
      throw new InvalidOrderSetError(`Invalid Order Set status: ${props.status}`);
    }
    if (props.draftBaseline !== undefined && props.status !== "draft") {
      throw new InvalidOrderSetError("Only a draft order set keeps the baseline it replaced", {
        orderSetId: props.id,
      });
    }
    if (props.draftBaseline) {
      const { envelopeMinutes, steps } = props.draftBaseline;
      OrderSet.restore({ ...props, envelopeMinutes, steps, status: "active", draftBaseline: undefined });
    }
    if (!props.patientId) {
      throw new InvalidOrderSetError("An order set requires a patient");
    }
    if (props.protocol && !(props.protocol.url && props.protocol.version)) {
      throw new InvalidOrderSetError("An order set names the protocol revision it was realized from, url and version");
    }
    if (!props.orderedAt || Number.isNaN(Date.parse(props.orderedAt))) {
      throw new InvalidOrderSetError("An order set names the instant its protocol was materialized onto it", {
        orderSetId: props.id,
      });
    }
    if ((props.carePlanId === undefined) !== (props.curationRank === undefined)) {
      throw new InvalidOrderSetError("A curated order set requires both its care plan and curation rank", {
        orderSetId: props.id,
      });
    }
    if (props.curationRank !== undefined && (!Number.isInteger(props.curationRank) || props.curationRank < 0)) {
      throw new InvalidOrderSetError("An order set curation rank must be a non-negative integer", {
        orderSetId: props.id,
      });
    }
    if (!Number.isSafeInteger(props.stepIdHigh) || props.stepIdHigh < 0) {
      throw new InvalidOrderSetError("An order set requires a non-negative step identity high-water mark", {
        orderSetId: props.id,
      });
    }
    const stepIds = new Set<MemberId>();
    for (const step of props.steps) {
      const number = /^step-([1-9]\d*)$/.exec(step.id)?.[1];
      if (!number || Number(number) > props.stepIdHigh || stepIds.has(step.id)) {
        throw new InvalidOrderSetError("Order steps require distinct issued member identities", {
          orderSetId: props.id,
        });
      }
      stepIds.add(step.id);
    }
    const seen = new Set<string>();
    for (const step of props.steps) {
      for (const order of step.orders) assertOrder(order, seen, props.id);
    }
    assertDuration(props.envelopeMinutes, props.id);
    this.state = {
      ...structuredClone(props),
      steps: props.steps.map((step) => ({
        id: step.id,
        orders: structuredClone(step.orders),
        placement: resolvePlacement(step.placement),
      })),
    };
    assertStepPlacements(this.state.steps, props.id);
    if (this.durationMinutes > ENVELOPE_DURATION_CAP_MINUTES) {
      throw new InvalidOrderSetError(`An order set cannot exceed ${ENVELOPE_DURATION_CAP_MINUTES} minutes`, {
        orderSetId: props.id,
      });
    }
    this.state.envelopeMinutes = this.durationMinutes;
  }

  static create(
    input: Omit<OrderSetProps, "status" | "steps" | "stepIdHigh"> & { steps: Omit<OrderStep, "id">[] },
  ): OrderSet {
    const state = structuredClone(input);
    const ids = memberIdSequence("step");
    const steps = state.steps.map((step) => ({ ...step, id: ids.next() }));
    return new OrderSet({ ...state, steps, stepIdHigh: ids.high, id: asOrderSetId(input.id), status: "active" });
  }

  /**
   * A set started empty, with no protocol, built procedure by procedure. It is a
   * draft that has never been active: Save makes it so, Discard removes it.
   */
  static createEmpty(input: Pick<OrderSetProps, "id" | "patientId" | "orderedAt" | "carePlanId" | "curationRank">) {
    return new OrderSet({
      ...structuredClone(input),
      envelopeMinutes: EMPTY_ORDER_SET_ENVELOPE_MINUTES,
      status: "draft",
      stepIdHigh: 0,
      steps: [],
    });
  }

  static restore(snapshot: OrderSetSnapshot): OrderSet {
    return new OrderSet(snapshot);
  }

  /** Construct a hypothetical set; this never opens the booked-set mutation gate. */
  proposeBookingShape(proposal: OrderSetBookingShape): OrderSet {
    const current = new Map(
      this.state.steps.flatMap((step) => step.orders.map((order) => [order.orderId, order] as const)),
    );
    // An order the shape leaves out would leave the aggregate, and the engine
    // deletes what the aggregate no longer holds. Withdrawing work is a
    // revocation, which keeps the request on record.
    const proposed = new Set(proposal.steps.flatMap((step) => step.orders.map((order) => order.orderId)));
    for (const orderId of current.keys()) {
      if (!proposed.has(orderId)) {
        throw new InvalidOrderSetError(`The proposal omits existing order '${orderId}'; revoke it instead`, {
          orderSetId: this.state.id,
          orderId,
        });
      }
    }
    // Work added to a set that already checked in belongs to that execution.
    // Without the stamp check-in already handed the others, charting refuses it.
    const stamp = this.visitId;
    const steps = proposal.steps.map((step) => ({
      ...step,
      orders: step.orders.map((order): Order => {
        const previous = current.get(order.orderId);
        if (previous) {
          if (order.definition || order.parameterValues)
            throw new InvalidOrderSetError("Existing orders accept scheduling changes only");
          return { ...previous };
        }
        if (!order.definition || !order.parameterValues)
          throw new InvalidOrderSetError(`New order '${order.orderId}' requires a definition and parameters`);
        return {
          orderId: order.orderId,
          definition: order.definition,
          parameterValues: order.parameterValues,
          status: "draft",
          ...(stamp ? { visitId: stamp } : {}),
        };
      }),
    }));
    const stepIdHigh = Math.max(
      this.state.stepIdHigh,
      ...steps.map((step) => Number(/^step-([1-9]\d*)$/.exec(step.id)?.[1] ?? 0)),
    );
    return OrderSet.restore({ ...this.snapshot(), steps, stepIdHigh, envelopeMinutes: proposal.envelopeMinutes });
  }

  /** Apply a freshly checked proposal without opening the structural-intent gate. */
  applyBookingShape(proposal: OrderSetBookingShape, visit?: Visit): void {
    const proposed = this.proposeBookingShape(proposal);
    const before = this.snapshot();
    const after = proposed.snapshot();
    const from = { envelopeMinutes: before.envelopeMinutes, steps: before.steps };
    const to = { envelopeMinutes: after.envelopeMinutes, steps: after.steps };
    if (JSON.stringify(from) === JSON.stringify(to)) return;
    this.state = proposed.state;
    this.recordChange({ kind: "BookingShapeApplied", from, to });
    if (visit && (visit.status === "checkedIn" || visit.status === "inProgress")) this.stamp(visit.id);
  }

  // ── facts ──

  get id(): OrderSetId {
    return this.state.id;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  get protocol(): Readonly<VersionedCanonical> | undefined {
    return this.state.protocol && { ...this.state.protocol };
  }

  /** Supply bookable placement; exam-only protocols retain their envelope with no procedure steps. */
  bookingSteps(): readonly OrderStep[] {
    if (this.state.status !== "active") {
      throw new InvalidOrderSetError("Only an active order set can be booked", { orderSetId: this.id });
    }
    if (this.state.steps.length > 0 && !this.everyOrder().some((order) => !isResolved(order))) {
      throw new InvalidOrderSetError("An order set requires standing clinical work before booking", {
        orderSetId: this.id,
      });
    }
    return this.steps;
  }

  get durationMinutes(): number {
    return Math.max(this.state.envelopeMinutes, ...this.state.steps.map((step) => step.placement.endMinutes));
  }

  setEnvelopeDuration(minutes: number, scheduling: OrderSetSchedulingFacts): void {
    this.assertSchedulingEditable(scheduling);
    assertDuration(minutes, this.id);
    const candidate = OrderSet.restore({ ...this.snapshot(), envelopeMinutes: minutes });
    if (candidate.durationMinutes !== minutes) {
      throw new InvalidOrderSetError("The envelope must contain all order steps", { orderSetId: this.id });
    }
    const from = this.state.envelopeMinutes;
    if (from === minutes) return;
    this.state.envelopeMinutes = minutes;
    this.recordChange({ kind: "EnvelopeSet", from, to: minutes });
  }

  /** Validate a candidate before changing live state or recording any delta. */
  private replaceSteps(steps: OrderStep[], stepIdHigh = this.state.stepIdHigh): { envelopeGrewBy?: number } {
    const before = this.state.envelopeMinutes;
    const candidate = OrderSet.restore({ ...this.snapshot(), steps, stepIdHigh });
    this.state = candidate.state;
    const growth = this.state.envelopeMinutes - before;
    return growth > 0 ? { envelopeGrewBy: growth } : {};
  }

  get orderedAt(): string {
    return this.state.orderedAt;
  }

  get carePlanId(): CarePlanId | undefined {
    return this.state.carePlanId;
  }

  get curationRank(): number | undefined {
    return this.state.curationRank;
  }

  get status(): OrderSetStatus {
    return this.state.status;
  }

  /** Copies. The steps are state, and handing out live objects would let a caller edit around the commands. */
  get steps(): readonly OrderStep[] {
    return this.state.steps.map(serializeStep);
  }

  /** Every order, flattened in step order. */
  get orders(): readonly Order[] {
    return structuredClone(this.everyOrder());
  }

  get visitId(): VisitId | undefined {
    return this.everyOrder().find((o) => o.visitId)?.visitId;
  }

  /**
   * Whether this requested procedure may be executed now.
   *
   * Child request status deliberately remains `draft`: the OrderSet starts
   * active and check-in is the execution stamp. Execution is
   * therefore the conjunction of those aggregate facts, not a synthetic
   * ServiceRequest activation.
   */
  isOrderExecutable(orderId: OrderId): boolean {
    const order = this.requireOrder(orderId);
    return this.state.status === "active" && Boolean(order.visitId) && !isResolved(order);
  }

  snapshot(): OrderSetSnapshot {
    const { steps, ...state } = this.state;
    return { ...structuredClone(state), steps: steps.map(serializeStep) };
  }

  // ── curation ──

  /** Scope a set to the care plan curating it and place it in that care plan's sequence. */
  placeInCuration(carePlanId: CarePlanId, rank: number): void {
    this.assertCurable();
    if (!carePlanId)
      throw new InvalidOrderSetError("A curated order set requires a care plan", { orderSetId: this.state.id });
    if (!Number.isInteger(rank) || rank < 0) {
      throw new InvalidOrderSetError("An order set curation rank must be a non-negative integer", {
        orderSetId: this.state.id,
      });
    }
    if (this.state.carePlanId && this.state.carePlanId !== carePlanId) {
      throw new InvalidOrderSetError(`Order set belongs to care plan '${this.state.carePlanId}', not '${carePlanId}'`, {
        orderSetId: this.state.id,
      });
    }
    this.state.carePlanId = carePlanId;
    this.state.curationRank = rank;
  }

  /** Remove a selected set from its care plan curation while keeping the request as revoked history. */
  removeFromCuration(scheduling: OrderSetSchedulingFacts): void {
    this.assertSchedulingEditable(scheduling);
    if (!this.state.carePlanId) {
      throw new InvalidOrderSetError("Only a care-plan-scoped order set can be removed from curation", {
        orderSetId: this.state.id,
      });
    }
    this.leaveDraft("revoked");
  }

  /**
   * Batch curation gates withdrawals as well as timing edits. The first change
   * to an active set opens a draft; a refused change leaves the set as it was.
   */
  applyChange(change: OrderSetCurationChange, scheduling: OrderSetSchedulingFacts): void {
    this.assertSchedulingEditable(scheduling);
    const opening = this.state.status === "active";
    if (opening) {
      this.state.draftBaseline = draftBaselineOf(this.snapshot());
      this.state.status = "draft";
    }
    try {
      this.curate(change);
    } catch (error) {
      if (opening) this.leaveDraft("active");
      throw error;
    }
  }

  /** Return a draft to booking, logging everything it changed as one act. */
  saveDraft(): void {
    const from = this.requireDraft();
    if (!this.everyOrder().some((order) => order.status !== "revoked")) {
      throw new InvalidOrderSetError("Add at least one procedure before saving", { orderSetId: this.state.id });
    }
    const to = draftBaselineOf(this.snapshot());
    this.leaveDraft("active");
    this.recordChange({ kind: "DraftSaved", ...(from ? { from } : {}), to });
  }

  /** Put back what the draft replaced; a set that has never been active is removed. */
  discardDraft(): void {
    const baseline = this.requireDraft();
    if (!baseline) {
      this.leaveDraft("revoked");
      return;
    }
    this.state = OrderSet.restore(restoreDraftBaseline(this.snapshot(), baseline)).state;
  }

  /** The baseline to restore, or undefined for a set that has never been active. */
  private requireDraft(): OrderSetDraftBaseline | undefined {
    if (this.state.status !== "draft") {
      throw new InvalidOrderSetError("This order set has no unsaved draft", { orderSetId: this.state.id });
    }
    return this.state.draftBaseline && structuredClone(this.state.draftBaseline);
  }

  private leaveDraft(status: Exclude<OrderSetStatus, "draft">): void {
    this.state.status = status;
    delete this.state.draftBaseline;
  }

  /** EVERY command's log entry passes here: while drafting, most are held for `DraftSaved`. */
  protected override recordChange(change: OrderSetChange): void {
    if (this.state.status === "draft" && !logsWhileDrafting(change, this.state.draftBaseline)) return;
    super.recordChange(change);
  }

  private curate(change: OrderSetCurationChange): void {
    switch (change.kind) {
      case "addOrder":
        this.addOrder(...change.args);
        return;
      case "revokeOrder":
        this.withdrawInCuration((candidate) => candidate.revokeOrder(...change.args));
        return;
      case "splitOrder":
        this.splitOrder(...change.args);
        return;
      case "moveOrder":
        this.moveOrder(...change.args);
        return;
      case "resizeStep":
        this.resizeStep(...change.args);
        return;
      case "reorderStep":
        this.reorderStep(...change.args);
        return;
      case "moveStep":
        this.moveStep(...change.args);
        return;
      case "setEnvelopeDuration":
        this.setEnvelopeDuration(...change.args);
        return;
      case "resetTiming":
        this.resetTiming(...change.args);
        return;
      case "replacePlannedTrials":
        this.replacePlannedTrials(...change.args);
        return;
    }
  }

  /** Stage a withdrawal on a copy before touching the aggregate or its change log. */
  private withdrawInCuration(withdraw: (candidate: OrderSet) => void): void {
    const candidate = OrderSet.restore(this.snapshot());
    withdraw(candidate);
    candidate.releaseWithdrawnStepTime();
    withdraw(this);
    this.state = candidate.state;
  }

  /** Curation releases withdrawn step allocations; direct clinical cancellation keeps timing unchanged. */
  private releaseWithdrawnStepTime(): void {
    const steps = this.snapshot().steps;
    const removed = new Set(
      steps.flatMap((step, index) =>
        step.orders.length > 0 &&
        step.orders.every((order) => order.status === "revoked") &&
        step.placement.durationMinutes > 0
          ? [index]
          : [],
      ),
    );
    if (!removed.size) return;
    const next = releaseStepTime(steps, removed, this.state.envelopeMinutes);
    const candidate = OrderSet.restore({ ...this.snapshot(), ...next });
    this.state = candidate.state;
  }

  /**
   * Request another procedure. Appended as its own step, or run concurrently
   * with an existing one.
   *
   * FROM THE CATALOG, WITH NO OCCURRENCE — that is what makes it curation-added
   * rather than protocol-derived, and it is why `sourceOccurrenceId` cannot be
   * passed here.
   */
  addOrder(
    order: Pick<Order, "orderId" | "parameterValues"> & { definition: MaterializableDefinition },
    declarations: readonly OrderParameterDeclaration[],
    opts: OrderSetSchedulingFacts & { concurrentWithStep?: number },
  ): void {
    this.assertSchedulingEditable(opts);
    const added: Order = {
      orderId: order.orderId,
      // Choosing a definition for new work is gated on the lineage being active
      // and the revision being current — the same gate an exam and a
      // questionnaire pass through. Hydration is deliberately not: `assertOrder`
      // takes the bare pin, so an order placed before the catalog moved loads.
      definition: materializeDefinition(order.definition),
      parameterValues: structuredClone(order.parameterValues),
      status: "draft",
      // Curation after check-in still belongs to the execution in progress.
      ...(this.visitId ? { visitId: this.visitId } : {}),
    };
    const seen = new Set(this.everyOrder().map((candidate) => candidate.orderId));
    assertOrder(added, seen, this.state.id);
    for (const answer of added.parameterValues) {
      this.assertDeclaredAnswer(added.orderId, answer.source, answer.parameterId, answer.value, declarations);
    }

    let steps = this.state.steps.map((step) => ({ ...step, orders: [...step.orders] }));
    const ids = memberIdSequence("step", this.state.stepIdHigh);
    let step = opts.concurrentWithStep;
    if (step === undefined) {
      const placement = resolvePlacement({ offsetMinutes: steps.at(-1)?.placement.endMinutes ?? 0 });
      steps.push({ id: ids.next(), placement, orders: [added] });
      step = steps.length - 1;
    } else {
      this.requireStep(step);
      steps = steps.map((candidate, index) =>
        index === step ? { ...candidate, orders: [...candidate.orders, added] } : candidate,
      );
    }
    const envelopeChange = this.replaceSteps(steps.map(serializeStep), ids.high);
    this.recordChange({
      kind: "OrderAdded",
      orderId: order.orderId,
      definition: added.definition,
      step,
      parameterValues: structuredClone(added.parameterValues),
      ...envelopeChange,
    });
  }

  /**
   * Withdraw a requested procedure. REVOKED, NEVER REMOVED — a request that was
   * made and retracted is history, and dropping the row loses that it was ever
   * asked for.
   *
   * NOT BLOCKED BY AN EXAM THAT REQUIRES IT. At authoring the exam-diagnostic
   * chain is unbreakable; in the room the machine breaks, the scan does not
   * happen, and the clinician still wants to do the rest of the exam. Authoring
   * guarantees the work is ORDERABLE, execution records what was DONE, and "it
   * didn't happen" is a clinical outcome rather than an error. The log records
   * that they did it, which is the point — visible rather than prevented.
   */
  revokeOrder(orderId: OrderId, revokedAt: string): void {
    this.assertCurable();
    const order = this.requireOrder(orderId);
    if (order.status === "completed") {
      throw new InvalidOrderSetError(`Completed order '${orderId}' cannot be revoked`, {
        orderSetId: this.state.id,
        orderId,
      });
    }
    // Idempotent, and silent the second time: a log entry for a revocation that
    // already happened is noise a reviewer has to filter.
    if (order.status === "revoked") return;
    // Revoking work must not silently re-time a visit. Authored placement stays
    // intact; the envelope floor already preserved the total before placement.
    order.status = "revoked";
    order.revokedAt = revokedAt;
    this.recordChange({ kind: "OrderRevoked", orderId });
  }

  /** Re-sequence one order, releasing its source allocation when no work remains there. */
  moveOrder(orderId: OrderId, toStep: number, scheduling: OrderSetSchedulingFacts): void {
    this.assertSchedulingEditable(scheduling);
    const fromStep = this.state.steps.findIndex((step) => step.orders.some((o) => o.orderId === orderId));
    const order = this.requireOrder(orderId);
    this.assertUnresolved(order, "re-sequenced");
    const target = this.requireStep(toStep);
    if (fromStep === toStep) return;
    const steps = this.snapshot().steps.map((step, index) => ({
      ...step,
      orders:
        index === fromStep
          ? step.orders.filter((candidate) => candidate.orderId !== orderId)
          : index === toStep
            ? [...step.orders, order]
            : step.orders,
    }));
    const vacated = steps[fromStep]?.orders.every((candidate) => candidate.status === "revoked");
    const released = releaseStepTime(steps, new Set(vacated ? [fromStep] : []), this.state.envelopeMinutes);
    const placed = released.steps
      .filter((step, index) => index !== fromStep || step.orders.length > 0)
      .map((step) => ({ ...step, placement: resolvePlacement(step.placement) }));
    const targetIndex = placed.findIndex((step) => step.id === target.id);
    const destination = placed[targetIndex];
    if (!destination) throw new InvalidOrderSetError("The destination step no longer exists", { orderSetId: this.id });
    const candidate = OrderSet.restore({
      ...this.snapshot(),
      steps: placed.map(serializeStep),
      envelopeMinutes: released.envelopeMinutes,
    });
    const growth = candidate.durationMinutes - this.durationMinutes;
    const envelopeChange = growth > 0 ? { envelopeGrewBy: growth } : {};
    this.state = candidate.state;
    this.recordChange({
      kind: "OrderMoved",
      orderId,
      fromStep,
      toStep,
      ...envelopeChange,
    });
  }

  /** Separate concurrent work without changing the order identity or its answers. */
  splitOrder(orderId: OrderId, scheduling: OrderSetSchedulingFacts): void {
    this.assertSchedulingEditable(scheduling);
    const order = this.requireOrder(orderId);
    this.assertUnresolved(order, "re-sequenced");
    const fromStep = this.state.steps.findIndex((step) => step.orders.some((item) => item.orderId === orderId));
    const source = this.requireStep(fromStep);
    if (source.orders.filter((item) => item.status !== "revoked").length < 2) {
      throw new PreconditionFailedError("Only a concurrent procedure can be moved to a new step");
    }
    const ids = memberIdSequence("step", this.state.stepIdHigh);
    const steps = this.state.steps.map((step, index) => ({
      ...step,
      orders: index === fromStep ? step.orders.filter((item) => item.orderId !== orderId) : [...step.orders],
    }));
    const toStep = fromStep + 1;
    steps.splice(toStep, 0, {
      id: ids.next(),
      placement: resolvePlacement({ offsetMinutes: source.placement.endMinutes }),
      orders: [order],
    });
    const envelopeChange = this.replaceSteps(settle(steps, toStep).map(serializeStep), ids.high);
    this.recordChange({ kind: "OrderMoved", orderId, fromStep, toStep, ...envelopeChange });
  }

  /** Reorder a whole step without changing its contents or reservation duration. */
  reorderStep(stepIndex: number, toStep: number, scheduling: OrderSetSchedulingFacts): void {
    this.assertSchedulingEditable(scheduling);
    const step = this.requireStep(stepIndex);
    this.requireStep(toStep);
    if (stepIndex === toStep) return;
    for (const affected of this.state.steps.slice(Math.min(stepIndex, toStep), Math.max(stepIndex, toStep) + 1)) {
      if (affected.orders.some((order) => order.status === "completed")) {
        throw new PreconditionFailedError("Steps containing completed work cannot be reordered");
      }
    }
    const next = reorderPlacedStep(this.state.steps, stepIndex, toStep);
    this.replaceSteps(next.map(serializeStep));
    this.recordChange({ kind: "StepReordered", stepId: step.id, from: stepIndex, to: toStep });
  }

  /** Keep the dropped position and settle collisions on both sides atomically. */
  moveStep(stepIndex: number, offsetMinutes: number, scheduling: OrderSetSchedulingFacts): void {
    this.assertSchedulingEditable(scheduling);
    const step = this.requireStep(stepIndex);
    const from = step.placement.offsetMinutes;
    if (from === offsetMinutes) return;
    const delta = offsetMinutes - from;
    const placement = step.placement.shiftedBy(delta);
    const next = replacePlacementAndSettle(this.state.steps, stepIndex, placement);
    const envelopeChange = this.replaceSteps(next.map(serializeStep));
    this.recordChange({
      kind: "StepMoved",
      step: stepIndex,
      from,
      to: offsetMinutes,
      ...envelopeChange,
    });
  }

  /** Restore protocol timing from resolved facts; curation-added work follows it. */
  resetTiming(facts: ResetTimingFacts, scheduling: OrderSetSchedulingFacts): void {
    this.assertSchedulingEditable(scheduling);
    const steps: Omit<OrderStep, "id">[] = [];
    for (const order of this.orders) {
      if (!order.sourceOccurrenceId) continue;
      const baseline = facts.placements.find((candidate) => candidate.orderId === order.orderId);
      if (!baseline) {
        throw new InvalidOrderSetError(`No protocol timing for order '${order.orderId}'`, { orderSetId: this.id });
      }
      let step = steps.find((candidate) => candidate.placement.offsetMinutes === baseline.placement.offsetMinutes);
      if (!step) {
        step = { placement: { ...baseline.placement }, orders: [] };
        steps.push(step);
      }
      if (step.placement.durationMinutes !== baseline.placement.durationMinutes) {
        throw new InvalidOrderSetError("Inconsistent protocol step placements", { orderSetId: this.id });
      }
      step.orders.push(order);
    }
    steps.sort((left, right) => left.placement.offsetMinutes - right.placement.offsetMinutes);
    const restored = steps.map((step) => ({ ...step, placement: resolvePlacement(step.placement) }));
    for (const [index, step] of restored.entries()) {
      const previous = restored[index - 1];
      if (previous && !previous.placement.precedes(step.placement)) {
        throw new InvalidOrderSetError("Protocol step placements must not overlap", { orderSetId: this.id });
      }
    }
    steps.splice(0, steps.length, ...restored.map(serializeStep));
    for (const current of this.steps) {
      const orders = current.orders.filter((order) => !order.sourceOccurrenceId);
      if (orders.length === 0) continue;
      const previous = steps.at(-1)?.placement;
      // In a mixed step only the protocol members return to their baseline.
      // Added members stay together and retain the step's authored duration.
      steps.push({
        placement: {
          offsetMinutes: previous ? previous.offsetMinutes + previous.durationMinutes : 0,
          durationMinutes: current.placement.durationMinutes,
        },
        orders,
      });
    }
    const timing = (items: readonly Omit<OrderStep, "id">[]): StepTiming[] =>
      items.map((step) => ({
        placement: { ...step.placement },
        orders: step.orders.map(({ orderId }) => ({ orderId })),
      }));
    const from = timing(this.steps);
    const to = timing(steps);
    // Reset the whole timing baseline, including protocol tail time. Validate
    // before mutating so a refused reset leaves both state and audit untouched.
    // A reset preserves a step only when its complete order membership survives.
    const membership = (step: { orders: readonly Order[] }): string =>
      JSON.stringify(step.orders.map((order) => order.orderId).sort());
    const existing = new Map(this.state.steps.map((step) => [membership(step), step.id]));
    const ids = memberIdSequence("step", this.state.stepIdHigh);
    const identified = steps.map((step) => ({ ...step, id: existing.get(membership(step)) ?? ids.next() }));
    const candidate = OrderSet.restore({
      ...this.snapshot(),
      steps: identified,
      stepIdHigh: ids.high,
      envelopeMinutes: facts.envelopeMinutes,
    });
    const before = this.durationMinutes;
    const after = candidate.durationMinutes;
    if (JSON.stringify(from) === JSON.stringify(to) && before === after) return;
    this.state = candidate.state;
    if (before !== after) this.recordChange({ kind: "EnvelopeSet", from: before, to: after });
    this.recordChange({ kind: "TimingReset", from, to, ...(after > before ? { envelopeGrewBy: after - before } : {}) });
  }

  /** Explicitly edit duration. Shrinkage leaves all later placements where authored. */
  resizeStep(stepIndex: number, durationMinutes: number, scheduling: OrderSetSchedulingFacts): void {
    this.assertSchedulingEditable(scheduling);
    const step = this.requireStep(stepIndex);
    const from = step.placement.durationMinutes;
    if (from === durationMinutes) return;
    const placement = step.placement.resizedTo(durationMinutes);
    const next = replacePlacementAndSettle(this.state.steps, stepIndex, placement);
    const envelopeChange = this.replaceSteps(next.map(serializeStep));
    this.recordChange({
      kind: "StepResized",
      step: stepIndex,
      from,
      to: durationMinutes,
      ...envelopeChange,
    });
  }

  /**
   * Re-specify how a requested procedure is to be carried out. One value at a
   * time, keyed by `(source, parameterId)` — the pair, not the id alone, or a
   * device's `p-1` overwrites the procedure's.
   *
   * Setting a parameter to the value it already holds is a NO-OP THAT LOGS
   * NOTHING. Only the entity can tell; that is the whole reason the log is
   * accumulated here rather than diffed by a handler.
   */
  setParameterValue(
    orderId: OrderId,
    source: ParameterSource,
    parameterId: MemberId,
    value: AnsweredValue | null,
    declarations: readonly OrderParameterDeclaration[],
  ): void {
    this.assertCurable();
    const order = this.requireOrder(orderId);
    this.assertUnresolved(order, "re-specified");
    if (order.plannedTrials?.length) {
      throw new InvalidOrderSetError(`Order '${orderId}' answers its parameters on its planned trials`, {
        orderSetId: this.state.id,
        orderId,
      });
    }
    this.assertDeclaredAnswer(orderId, source, parameterId, value, declarations);
    const existing = order.parameterValues.find((p) => p.source === source && p.parameterId === parameterId);
    if (value === null) {
      if (!existing) return;
      order.parameterValues = order.parameterValues.filter((answer) => answer !== existing);
      this.recordChange({
        kind: "ParameterChanged",
        orderId,
        source,
        parameterId,
        from: structuredClone(existing.value),
        to: null,
      });
      return;
    }
    if (existing && answersEqual(existing.value, value)) return;

    const from = existing ? structuredClone(existing.value) : undefined;
    if (existing) existing.value = structuredClone(value);
    else order.parameterValues.push({ source, parameterId, value: structuredClone(value) });
    this.recordChange({ kind: "ParameterChanged", orderId, source, parameterId, from, to: structuredClone(value) });
  }

  /**
   * Re-plan an order's trials as a whole list, each row keyed by its id. Not
   * offered on an order that answers parameters itself: replacing those would
   * drop them, and folding them into a trial is the author's call.
   */
  replacePlannedTrials(
    orderId: OrderId,
    trials: readonly PlannedTrial[],
    declarations: readonly OrderParameterDeclaration[],
  ): void {
    this.assertCurable();
    const order = this.requireOrder(orderId);
    this.assertUnresolved(order, "re-planned");
    if (order.parameterValues.length) {
      throw new InvalidOrderSetError(`Order '${orderId}' answers parameters itself and cannot carry planned trials`, {
        orderSetId: this.state.id,
        orderId,
      });
    }
    const problem = plannedTrialsProblem(trials);
    if (problem) throw new InvalidOrderSetError(problem, { orderSetId: this.state.id, orderId });
    for (const trial of trials) {
      for (const answer of trial.values) {
        this.assertDeclaredAnswer(orderId, answer.source, answer.parameterId, answer.value, declarations);
      }
    }
    const from = order.plannedTrials ?? [];
    if (plannedTrialsEqual(from, trials)) return;
    if (trials.length) order.plannedTrials = structuredClone([...trials]);
    else delete order.plannedTrials;
    this.recordChange({
      kind: "PlannedTrialsReplaced",
      orderId,
      from: structuredClone(from),
      to: structuredClone([...trials]),
    });
  }

  // ── lifecycle ──

  complete(): void {
    if (this.state.status === "revoked") {
      throw new InvalidOrderSetError("A revoked order set cannot be completed", { orderSetId: this.id });
    }
    const open = this.everyOrder().filter((order) => !isResolved(order));
    if (open.length) {
      throw new InvalidOrderSetError(
        `Order set cannot be completed with open work: ${open.map((order) => order.orderId).join(", ")}`,
        { orderSetId: this.id },
      );
    }
    this.leaveDraft("completed");
  }

  /**
   * Withdraw the whole set. EXPLICIT AND ONLY FOR THE WHOLE SET — `revoked`
   * must mean someone withdrew it. Inferring it from the orders instead makes
   * a deliberate withdrawal and a set whose orders all happened to be revoked
   * indistinguishable afterwards.
   */
  revoke(): void {
    if (this.state.status === "revoked") return;
    this.leaveDraft("revoked");
  }

  /**
   * Record that one order reached its terminal clinical outcome. The RESULT
   * lives in its own record; this is the request side learning that the request
   * is answered.
   */
  completeOrder(orderId: OrderId): void {
    const order = this.requireOrder(orderId);
    if (order.status === "revoked") {
      throw new InvalidOrderSetError(`Revoked order '${orderId}' cannot be completed`, {
        orderSetId: this.state.id,
        orderId,
      });
    }
    order.status = "completed";
  }

  /** Revoke an unfulfilled request when charting records that it was not performed. */
  markOrderDidNotPerform(orderId: OrderId, revokedAt: string): void {
    const order = this.requireOrder(orderId);
    if (order.status === "completed") {
      throw new InvalidOrderSetError(`Completed order '${orderId}' cannot be revoked`, {
        orderSetId: this.state.id,
        orderId,
      });
    }
    if (order.status === "revoked") return;
    // Revoking work must not silently re-time a visit. Authored placement stays
    // intact; the envelope floor already preserved the total before placement.
    order.status = "revoked";
    order.revokedAt = revokedAt;
    this.recordChange({ kind: "OrderNotPerformed", orderId });
  }

  /**
   * Undo `markOrderDidNotPerform`. SEPARATE FROM `reopenOrder` because a
   * revocation has two causes that look identical on the order: a clinician
   * withdrawing the request, which stands, and charting recording that the work
   * did not happen, which they may take back. Only the charting side has a
   * record to reverse, so only its caller can tell them apart — the guard is
   * the did-not-perform procedure the handler loads before calling this.
   */
  restoreNotPerformedOrder(orderId: OrderId): void {
    const order = this.requireOrder(orderId);
    if (order.status !== "revoked") {
      throw new InvalidOrderSetError(`Order '${orderId}' is '${order.status}' and was not recorded as not performed`, {
        orderSetId: this.state.id,
        orderId,
      });
    }
    // Restoring revoked work can make a previously valid extent too short.
    // Validate the candidate before changing status or clearing clinical history.
    this.replaceSteps(
      this.steps.map((step) => ({
        ...step,
        orders: step.orders.map((candidate) =>
          candidate.orderId === orderId ? { ...candidate, status: "draft" as const, revokedAt: undefined } : candidate,
        ),
      })),
    );
    if (this.state.status === "completed") this.state.status = "active";
  }

  reopenOrder(orderId: OrderId): void {
    const order = this.requireOrder(orderId);
    if (order.status === "revoked") {
      throw new InvalidOrderSetError(`Revoked order '${orderId}' cannot be reopened`, {
        orderSetId: this.state.id,
        orderId,
      });
    }
    if (order.status !== "completed") {
      throw new InvalidOrderSetError(`Order '${orderId}' is '${order.status}' and cannot be reopened`, {
        orderSetId: this.state.id,
        orderId,
      });
    }
    order.status = "draft";
    if (this.state.status === "completed") this.state.status = "active";
  }

  /**
   * Link unresolved orders to the checked-in visit so charting can find them.
   * Set and order statuses remain unchanged. Withdrawn and completed orders
   * retain their existing execution associations.
   */
  stamp(visitId: VisitId): void {
    const current = this.visitId;
    if (current && current !== visitId) {
      throw new InvalidOrderSetError(
        `Order set is already linked to visit '${current}' and cannot move to '${visitId}'`,
        { orderSetId: this.state.id },
      );
    }
    for (const order of this.everyOrder()) {
      if (isResolved(order)) continue;
      order.visitId = visitId;
    }
  }

  // ── internals ──

  /** Live references in step order — mutable, unlike the `orders` accessor. */
  private everyOrder(): Order[] {
    return this.state.steps.flatMap((step) => step.orders);
  }

  private requireOrder(orderId: OrderId): Order {
    const order = this.everyOrder().find((o) => o.orderId === orderId);
    if (!order) {
      throw new InvalidOrderSetError(`Order '${orderId}' is not in this set`, { orderSetId: this.state.id, orderId });
    }
    return order;
  }

  private assertDeclaredAnswer(
    orderId: OrderId | undefined,
    source: ParameterSource,
    parameterId: MemberId,
    value: AnsweredValue | null,
    declarations: readonly OrderParameterDeclaration[],
  ): void {
    const declaration = declarations.find(
      (candidate) => candidate.source === source && candidate.parameterId === parameterId,
    );
    if (!declaration) {
      throw new InvalidOrderSetError(
        `Order '${orderId}' definition does not declare parameter '${parameterId}' from '${source}'`,
        { orderSetId: this.state.id, orderId },
      );
    }
    if (value === null) return;
    const problem = answeredValueProblem(declaration.value, value);
    if (problem) {
      throw new InvalidOrderSetError(`Parameter '${parameterId}' ${problem}`, {
        orderSetId: this.state.id,
        orderId,
      });
    }
  }

  private requireStep(index: number): PlacedOrderStep {
    const step = this.state.steps[index];
    if (!step) {
      throw new InvalidOrderSetError(`Step ${index} is not in this set`, { orderSetId: this.state.id, step: index });
    }
    return step;
  }

  private assertUnresolved(order: { orderId: OrderId; status: OrderStatus }, verb: string): void {
    if (isResolved(order)) {
      throw new InvalidOrderSetError(`Order '${order.orderId}' is resolved and cannot be ${verb}`, {
        orderSetId: this.state.id,
        orderId: order.orderId,
      });
    }
  }

  /** Interior claims change even when resequencing leaves the total unchanged. */
  assertSchedulingEditable(scheduling: OrderSetSchedulingFacts): void {
    this.assertCurable();
    if (scheduling.booked) {
      throw new InvalidOrderSetError("A booked order set cannot change its duration or sequence", {
        orderSetId: this.id,
      });
    }
  }

  /** A terminal set is history. Curating it would be editing what already happened. */
  private assertCurable(): void {
    if (this.state.status === "completed" || this.state.status === "revoked") {
      throw new InvalidOrderSetError(`A ${this.state.status} order set cannot be curated`, {
        orderSetId: this.state.id,
      });
    }
  }
}

function isResolved(order: { status: OrderStatus }): boolean {
  return order.status === "completed" || order.status === "revoked";
}

/**
 * What bookability reads. Structural rather than the model types, so one
 * derivation serves a plain facts object, a read-side DTO and an `OrderSet`
 * without either one knowing about the other.
 */
export interface BookableSetFacts {
  status: OrderSetStatus;
}

export interface BookableOrderFacts {
  status: OrderStatus;
}

/**
 * New sets are active. Terminal sets cannot be booked; clinical prerequisites
 * come from the associated case and care plan. Child requests remain `draft`
 * until resolved; execution eligibility uses the check-in stamp (see
 * `isOrderExecutable`). Booking precedes check-in, so it does not require a `visitId`.
 */
export function isOrderBookable(set: BookableSetFacts, order: BookableOrderFacts, scheduled: boolean): boolean {
  return set.status === "active" && !isResolved(order) && !scheduled;
}

function assertOrder(order: Order, seen: Set<string>, orderSetId: OrderSetId | undefined): void {
  if (!(order.definition?.url && order.definition.version)) {
    throw new InvalidOrderSetError("An order pins the catalog revision it was realized from, url and version", {
      orderSetId,
      orderId: order.orderId,
    });
  }
  const keys = new Set<string>();
  for (const parameter of order.parameterValues) {
    // Two answers to one parameter is not "the later one wins" — it is a set
    // that cannot say what was ordered, which is the failure the source/id pair
    // exists to make impossible in the first place.
    const key = `${parameter.source}\0${parameter.parameterId}`;
    if (keys.has(key)) {
      throw new InvalidOrderSetError(`Parameter '${parameter.parameterId}' is answered twice on one order`, {
        orderSetId,
        orderId: order.orderId,
        parameterId: parameter.parameterId,
      });
    }
    keys.add(key);
  }
  if (order.plannedTrials?.length && order.parameterValues.length) {
    throw new InvalidOrderSetError("An order with planned trials cannot answer parameters itself", {
      orderSetId,
      orderId: order.orderId,
    });
  }
  const problem = plannedTrialsProblem(order.plannedTrials ?? []) ?? observationLevelsProblem(order.observationLevels);
  if (problem) throw new InvalidOrderSetError(problem, { orderSetId, orderId: order.orderId });
  assertOrderIdUnique(order, seen, orderSetId);
}

function assertOrderIdUnique(order: { orderId: OrderId }, seen: Set<string>, orderSetId: OrderSetId | undefined): void {
  if (!order.orderId) {
    throw new InvalidOrderSetError("Every order requires a stable domain identity", { orderSetId });
  }
  if (seen.has(order.orderId)) {
    throw new InvalidOrderSetError(`Order '${order.orderId}' appears more than once in the set`, {
      orderSetId,
      orderId: order.orderId,
    });
  }
  seen.add(order.orderId);
}

/** The visit envelope must reserve a positive grid duration. */
function assertDuration(duration: number, orderSetId: OrderSetId | undefined): void {
  if (
    !Number.isFinite(duration) ||
    duration <= 0 ||
    duration % ENVELOPE_DURATION_INCREMENT_MINUTES !== 0 ||
    duration > ENVELOPE_DURATION_CAP_MINUTES
  ) {
    throw new InvalidOrderSetError(
      `A duration must be a positive multiple of 5 no greater than ${ENVELOPE_DURATION_CAP_MINUTES} minutes, got ${duration}`,
      {
        orderSetId,
      },
    );
  }
}

/** Value objects stay inside the aggregate; callers receive isolated plain minute props. */
function serializeStep<T extends { placement: StepPlacement; orders: Order[] }>(
  step: T,
): Omit<T, "placement"> & { placement: StepPlacementProps } {
  return {
    ...step,
    placement: { offsetMinutes: step.placement.offsetMinutes, durationMinutes: step.placement.durationMinutes },
    orders: structuredClone(step.orders),
  };
}

/** Storage and mutation candidates share the same placement invariants. */
function assertStepPlacements(steps: readonly PlacedOrderStep[], orderSetId: OrderSetId): void {
  let previousStep: PlacedOrderStep | undefined;
  for (const [index, step] of steps.entries()) {
    if (previousStep !== undefined && !previousStep.placement.precedes(step.placement)) {
      throw new InvalidOrderSetError("Order step placements must be ordered and must not overlap", {
        orderSetId,
        step: index,
      });
    }
    previousStep = step;
    if (
      step.placement.durationMinutes === 0 &&
      (!step.orders.length || step.orders.some((order) => order.status !== "revoked"))
    ) {
      throw new InvalidOrderSetError("Only withdrawn historical steps may have no allocated time", {
        orderSetId,
        step: index,
      });
    }
    // Empty steps retain an authored extent, so hydration preserves the hole
    // left by a move instead of compacting the timeline on the next read.
  }
}

/** Keep sequence identity when a drop crosses neighbouring offsets, then settle collisions. */
function replacePlacementAndSettle(
  steps: readonly PlacedOrderStep[],
  index: number,
  placement: StepPlacement,
): PlacedOrderStep[] {
  const candidates = steps.map((step, stepIndex) => {
    if (stepIndex === index) return { ...step, placement };
    // Clamp crossed offsets to the drop so the shared helper receives ordered
    // input without reordering step identities. settle places each crossed step
    // flush on its own side; it owns the displacement and boundary checks.
    if (
      (stepIndex > index && step.placement.offsetMinutes < placement.offsetMinutes) ||
      (stepIndex < index && step.placement.offsetMinutes > placement.offsetMinutes)
    ) {
      return { ...step, placement: step.placement.shiftedBy(placement.offsetMinutes - step.placement.offsetMinutes) };
    }
    return step;
  });
  return settle(candidates, index);
}

export type OrderSetCurationChange =
  | { kind: "addOrder"; args: Parameters<OrderSet["addOrder"]> }
  | { kind: "revokeOrder"; args: Parameters<OrderSet["revokeOrder"]> }
  | { kind: "splitOrder"; args: Parameters<OrderSet["splitOrder"]> }
  | { kind: "moveOrder"; args: Parameters<OrderSet["moveOrder"]> }
  | { kind: "resizeStep"; args: Parameters<OrderSet["resizeStep"]> }
  | { kind: "reorderStep"; args: Parameters<OrderSet["reorderStep"]> }
  | { kind: "moveStep"; args: Parameters<OrderSet["moveStep"]> }
  | { kind: "setEnvelopeDuration"; args: Parameters<OrderSet["setEnvelopeDuration"]> }
  | { kind: "resetTiming"; args: Parameters<OrderSet["resetTiming"]> }
  | { kind: "replacePlannedTrials"; args: Parameters<OrderSet["replacePlannedTrials"]> };

/** Stable order member key; includes UUIDs and inherited protocol occurrence keys. */
export type OrderId = Brand<string, "OrderId">;
export function asOrderId(value: string): OrderId {
  if (!value) throw new PreconditionFailedError("An order identity cannot be empty");
  return value as OrderId;
}
